import { useState, useEffect, useRef } from "react";
import { useHeaderContext } from "../../context/headerContext";
import { useThemeContext } from "../../context/themeContext";
import { useLocation, useNavigate } from "react-router-dom";
import Tabs from "../../components/Tabs";
import ActivityCard from "../../components/ActivityCard";
import ActivitySearch from "../../components/ActivitySearch";
import { useUserContext } from "../../context/userContext";
import SelectInput from "../../components/SelectInput";
import { formatDistanceToNowStrict } from "date-fns";
import Button from "../../components/Button";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function Activities() {
  const { setTitle, setBackButton, setSearchFunc, setSearchTriggered, searchTriggered } = useHeaderContext();
  const [searchOverlayVisible, setSearchOverlayVisible] = useState(false);
  const { theme } = useThemeContext();
  const [activities, setActivities] = useState([]);
  const [activityError, setActivityError] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user, participants, logout, updateTokens, selectedActivityLogParticipant, setSelectedActivityLogParticipant } = useUserContext();
  const location = useLocation();
  const navigate = useNavigate();

  const reversedParticipants = participants?.slice().reverse();
  const defaultParticipantId = location.state?.selectedParticipant || selectedActivityLogParticipant || (reversedParticipants?.length > 0 ? reversedParticipants[0].ParticipantId : '');

  const [selectedParticipant, setSelectedParticipant] = useState(defaultParticipantId);

  const [currentTab, setCurrentTab] = useState("log");
  const [searchCriteria, setSearchCriteria] = useState({
    search: "",
    activities: [],
    previousIncident: "unset",
    manualActivity: "unset",
    dateFrom: "",
    dateTo: "",
  });

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(null);
  const [activityTypes, setActivityTypes] = useState(null);

  useEffect(() => {
      setSelectedActivityLogParticipant(selectedParticipant);
  }, [selectedParticipant]);

  const getActivityTypes = async () => {
    await Sentry.startSpan({ name: "Fetch Activity Types", op: "http.request" }, async (span) => {
      try {
        const response = await fetchWithRefresh(
          `${import.meta.env.VITE_API_URL}Activity/FetchBaseActivities`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => {
            toast.info(message);
          }
        );
  
        span.setAttribute("http.status_code", response.status);
  
        if (response.ok) {
          const data = await response.json();
          setActivityTypes(data);
        } else {
          Sentry.captureMessage("Failed to fetch activity types", { extra: { status: response.status } });
          throw new Error("Failed to fetch activity types");
        }
      } catch (error) {
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error fetching activity types: " + error);
        span.setStatus("error");
      } finally {
        span.end();
      }
    });
  };
  
  useEffect(() => {
    getActivityTypes();
  }, []);

  const searchActivities = async (page = 0, loadMore = false) => {
    setActivityError(false);
    setCurrentPage(page);
  
    await Sentry.startSpan({ name: "Search Participant Activities", op: "http.request" }, async (span) => {
      try {
        const tempActivityTypes = searchCriteria?.activities.length > 0 ? searchCriteria?.activities : activityTypes;
        const stringActivities = (searchCriteria?.activities.length < 1 && searchCriteria?.manualActivity == 1)
          ? ""
          : tempActivityTypes?.map(activity => activity.BaseActivityId).join(',');
  
        const url = `${import.meta.env.VITE_API_URL}Activity/SearchParticipantActivityListByActivity?participantId=${selectedParticipant}${
          searchCriteria?.search ? `&venuematch=${searchCriteria?.search}` : ""
        }${searchCriteria?.previousIncident !== "unset" ? (searchCriteria?.previousIncident ? `&withIncidents=1` : `&withIncidents=0`) : ""}${
          searchCriteria?.manualActivity !== "unset" ? (searchCriteria?.manualActivity ? `&includeManual=1` : `&includeManual=0`) : "&includeManual=1"
        }&activitytypes=${stringActivities}${
          searchCriteria?.dateFrom && searchCriteria?.dateFrom !== "unset" ? `&datestart=${searchCriteria?.dateFrom}` : ""
        }${searchCriteria?.dateTo && searchCriteria?.dateTo !== "unset" ? `&dateend=${searchCriteria?.dateTo}` : ""}&page=${page}`;
  
        span.setAttribute("http.url", url);
  
        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        span.setAttribute("http.status_code", response.status);
  
        if (response.ok) {
          const data = await response.json();
          //console.log(JSON.stringify(data,null,2));

          /* if (loadMore) {
            setActivities((prevActivities) => [...prevActivities, ...data.participantactivitylist]);
          } else {
            setTotalPages(Math.ceil(data?.recordcount?.recordCount / data?.recordcount?.pageSize));
            setActivities(data.participantactivitylist);
          } */

          const normalizedList = data.participantactivitylist.map(normalizeActivity);

          if (loadMore) {
            setActivities((prevActivities) => [...prevActivities, ...normalizedList]);
          } else {
            setTotalPages(Math.ceil(data?.recordcount?.recordCount / data?.recordcount?.pageSize));
            setActivities(normalizedList);
          }


          setLoading(false);
        } else {
          Sentry.captureMessage("Failed to fetch activities", { extra: { status: response.status, url } });
          setActivityError("There was an error fetching the activities for this participant.");
          setActivities([]);
          throw new Error("Failed to fetch activities");
        }
      } catch (error) {
        Sentry.captureException(error);
        setActivityError("There was an error fetching the activities for this participant.");
        setActivities([]);
        LogErrors(user, logout, updateTokens, error);
        span.setStatus("error");
      } finally {
        span.end();
      }
    });
  };  

  useEffect(() => {
    if(selectedParticipant && activityTypes){
      searchActivities();
    }
  }, [selectedParticipant, searchCriteria, activityTypes]);

  const toggleSearchOverlay = () => {
    setSearchOverlayVisible(!searchOverlayVisible);
  };

  useEffect(() => {
    setTitle("Activity");
    setBackButton(true);
    setSearchFunc(() => {
      toggleSearchOverlay(searchOverlayVisible);
      setSearchTriggered(!searchTriggered);
    });

    if(!activityTypes){
      getActivityTypes();
    }
  }, [searchOverlayVisible]);

  const [inViewItems, setInViewItems] = useState([]);
  const activityListRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const listElement = activityListRef.current;
      if (!listElement) return;

      const items = Array.from(listElement.querySelectorAll(".activity-card"));
      const windowHeight = window.innerHeight;
      const windowScrollTop = window.scrollY;

      const newInViewItems = items
        .filter((item) => {
          const itemRect = item.getBoundingClientRect();
          const itemTop = itemRect.top + windowScrollTop;
          const itemBottom = itemTop + itemRect.height;

          return (
            itemTop >= windowScrollTop &&
            itemBottom <= windowScrollTop + windowHeight
          );
        })
        .map((item) => item.className.replace("activity-card relative ", ""));

      // Only update state if the new in-view items are different
      setInViewItems((prevInViewItems) => {
        if (JSON.stringify(prevInViewItems) !== JSON.stringify(newInViewItems)) {
          return newInViewItems;
        }
        return prevInViewItems;
      });
    };

    // Attach scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Initial check
    handleScroll();

    // Clean up event listener on unmount
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate time ago for activities[0]?.endTime
  //const endTime = activities[0]?.endTime;
  //const timeAgo = endTime ? formatDistanceToNowStrict(new Date(endTime)) : "Unknown time";

  //IOS 15 expects a date time string to have a T in it i.e. in strict ISO format
  const endTime = normalizeDateString(activities[0]?.endTime);
  const timeAgo = isValidDate(endTime)
  ? formatDistanceToNowStrict(new Date(endTime))
  : "Unknown time";


  function normalizeDateString(dateStr) {
    if (typeof dateStr !== 'string' || !dateStr.trim()) return null;
    return dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
  }

  function isValidDate(dateStr) {
    if (!dateStr) return false;
    const parsed = new Date(dateStr);
    return !isNaN(parsed.getTime());
  }

  const normalizeActivity = (activity) => ({
  ...activity,
  startTime: normalizeDateString(activity.startTime),
  endTime: normalizeDateString(activity.endTime),
  whenHappened: normalizeDateString(activity.whenHappened),
  collectableDataPoints: activity.collectableDataPoints?.map((dp) => ({
    ...dp,
    dataPointDate: normalizeDateString(dp.dataPointDate),
  })) || [],
});



  const handleActivitySelection = (activityName) => {
    setSearchCriteria((prevCriteria) => {
      const activities = prevCriteria.activities.includes(activityName)
        ? prevCriteria.activities.filter((name) => name !== activityName)
        : [...prevCriteria.activities, activityName];
      return { ...prevCriteria, activities };
    });
  };

  const handleParticipantChange = (e) => {
    const newParticipantId = e.target.value;
    setSelectedParticipant(newParticipantId);
    navigate('.', { 
      replace: true, 
      state: { selectedParticipant: newParticipantId } 
    });
  };

  return (
    <div className="flex flex-col h-full mb-[40px]">
      <Tabs
        tabs={["log", "milestones"]}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        selectedParticipant={selectedParticipant}
      />

      <div className="bg-sportsLightGrey pt-7 pl-5 pr-7 pb-8 flex flex-col h-full">
        <div className="mb-8">
          <p className="font-poppins text-[12px] font-semibold leading-6 spacing tracking-[0.2em] mb-1">
            LAST UPDATED • <span>{endTime ? timeAgo.toUpperCase() + " AGO" : "NO ACTIVITIES FOUND"}</span>
          </p>
          <p className="font-poppins text-[32px] font-semibold leading-[42px]">
            Your activity
          </p>
        </div>

        {participants?.length > 0 && (
          <div className="flex flex-col gap-5">
            <p className="font-semibold">Participant</p>
            <SelectInput
              onChange={handleParticipantChange}
              name="participantPicker"
              value={selectedParticipant}
              options={reversedParticipants?.map((participant, index) => (
                <option value={participant.ParticipantId} key={index}>
                  {participant.ParticipantName}
                </option>
              ))}
            />
            {loading || !activityTypes ? (
              <div className="flex justify-center items-center h-full min-h-[200px]">
                <div className="loading loading-spinner loading-lg">Loading...</div>
              </div>
            ) : (
            <div
              className={`activity-list
              flex flex-col gap-2 border-l-4 pl-[26px] ml-[10px] relative ${
                theme === "staff" ? "border-sportsBlue" : "border-sportsYellow"
              }
              before:top-0 before:left-[-14px] before:bg-sportsLightGrey before:w-6 before:h-3 before:absolute  
            `}
              ref={activityListRef}
            >

            {activities?.length > 0 ? (
              activities?.map((activity, index) => {
                const activityLink = "/activity/" + activity.activityId;
                const activityInView = inViewItems.includes(
                  "activityID" + activity.activityId
                );

                return (
                  <ActivityCard
                    href={activityLink}
                    background="#ffffff"
                    activity={activity}
                    key={activity.activityId}
                    timeline={true}
                    inView={activityInView}
                    dataPoints={activity?.collectableDataPoints}
                    subscription={user?.subscription?.Name}
                  />
                );
              })
            ) : activityError ? (
              <p>{activityError}</p>
            ) : (
              <p>There are no activities recorded for the selected participant.</p>
            )}
            </div>
        )}
            
            {(totalPages - 1) > currentPage && (
              <Button onClick={() => searchActivities((currentPage + 1), true)}>Load More</Button>
            )}
          </div>
        )}

      </div>
      {searchOverlayVisible && (
        <ActivitySearch
          searchCriteria={searchCriteria}
          activityTypes={activityTypes}
          setSearchCriteria={setSearchCriteria}
          setSearchOverlayVisible={setSearchOverlayVisible}
          handleActivitySelection={handleActivitySelection} // Pass the handler to ActivitySearch
          setCurrentPage={setCurrentPage}
        />
      )}
    </div>
  );
}